import { Box } from '@mui/material';
import { useFetch } from '../../../../../hooks/useFetch';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { object, string } from 'yup';
import { FormProvider } from '@sm-dashboard/form-provider';
import { getUser } from '../../../../../util/getUser';
import { editGeomUrl } from '../../../../../constants';
import { useEffect } from 'react';
import { useAppContext } from '../../../../../context/context';
import { Input, Select, Dialog, Typography } from '@sm-dashboard/ui-components';
import { useGoogleMap } from '@react-google-maps/api';
import MapPrint from '../../MapPrint/MapPrint';

export enum PopUpTypes {
  START_EDIT_POP_UP = 'start_edit_pop_up',
  FORM_EDIT_POP_UP = 'form_edit_pop_up',
  REPORT_FORM = 'report_form',
  PRINT_MAP = 'print_map'
}

export type PopUpStateType =
  | PopUpTypes.FORM_EDIT_POP_UP
  | PopUpTypes.START_EDIT_POP_UP
  | PopUpTypes.REPORT_FORM
  | PopUpTypes.PRINT_MAP
  | '';

type EditPopUpProps = {
  popUpState: PopUpStateType;
  setPopUpState: React.Dispatch<React.SetStateAction<PopUpStateType>>;
  polygons: google.maps.Polygon[] | undefined;
  drawingManager: google.maps.drawing.DrawingManager;
  setStartEdit: React.Dispatch<React.SetStateAction<boolean>>;
};

type FormValuesProps = {
  requestType: string;
  branchType: string;
  branchName: string;
  comment: string;
};

const schema = object({
  requestType: string().required(),
  branchType: string().required(),
  branchName: string().required(),
  comment: string()
}).required();

const EditPopUp = ({ drawingManager, setStartEdit, popUpState, setPopUpState, polygons }: EditPopUpProps) => {
  const map = useGoogleMap();
  const handleClose = () => {
    setPopUpState('');
    setStartEdit(false);
    methods.reset();
    polygons?.forEach(poly => poly.setVisible(false));
  };

  const handleStartEditing = () => {
    drawingManager.setMap(map);
    setStartEdit(true);
    setPopUpState('');
  };

  const { control } = useFormContext() || {};
  const { data, error, loading, fetchData } = useFetch();
  const user = getUser();
  const { setSnackbar } = useAppContext();

  const methods = useForm<FormValuesProps, Event>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    defaultValues: {
      requestType: '',
      branchType: '',
      branchName: user?.Branch?.branchName || '',
      comment: ''
    }
  });

  const { handleSubmit } = methods;

  const onSubmit = (data: FormValuesProps) => {
    if (polygons) {
      fetchData({
        method: 'POST',
        url: editGeomUrl,
        data: {
          ...data,
          coordinates: polygons.map(poly =>
            poly
              .getPaths()
              .getArray()[0]
              .getArray()
              .map(path => {
                return [path.lng(), path.lat()];
              })
          )
        }
      });
    }
  };

  useEffect(() => {
    if (data) {
      setSnackbar({ severity: 'success', message: 'בקשת שינוי תיחום נשלחה בהצלחה', open: true });
      handleClose();
    }

    if (error) {
      setSnackbar({ severity: 'error', message: 'שליחת שינוי תיחום נכשלה ', open: true });
      handleClose();
    }
  }, [data, error]);

  switch (popUpState) {
    case PopUpTypes.START_EDIT_POP_UP:
      return (
        <Dialog
          open={true}
          title="הנפקת טופס מפה חתומה"
          cancelAction={{ onClick: handleClose }}
          primaryAction={{ label: 'החל בשרטוט על המפה', onClick: handleStartEditing, autoFocus: true }}
        >
          <Typography variant="body" color="text.secondary">
            באפשרותך להפיק מפה חתומה לצורך שליחה לאגף הסניפים: לחץ על נקודה במפה ומתח ממנה קווים לפי התיחום הרצוי. לסיום
            השרטוט הקלק פעמיים על המפה. חשוב להקפיד על שרטוט ישר ומדיוק על פי הרחובות
          </Typography>
        </Dialog>
      );
    case PopUpTypes.FORM_EDIT_POP_UP:
      return (
        <Dialog
          fullWidth={true}
          open={true}
          onClose={handleClose}
          title="טופס עדכון / הוספה תיחום חדש"
          cancelAction
          primaryAction={{ label: 'שליחה', onClick: handleSubmit(onSubmit), loading, autoFocus: true }}
        >
          <FormProvider onSubmit={handleSubmit(onSubmit)} methods={methods}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Controller
                name="requestType"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Select
                    {...field}
                    appearance="plain"
                    label="סוג בקשה"
                    error={!!error}
                    options={[
                      { value: '1', label: 'עדכון תיחום סניף קיים - ' + user?.Branch?.branchName || '' },
                      { value: '2', label: 'הוספה של תיחום סניף חסר' }
                    ]}
                  />
                )}
                rules={{ required: true }}
              />
              <Controller
                name="branchType"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Select
                    {...field}
                    appearance="plain"
                    label="סוג תיחום"
                    error={!!error}
                    options={[
                      { value: '1', label: 'עיר' },
                      { value: '2', label: 'מועצה אזורית' },
                      { value: '3', label: 'ישוב' },
                      { value: '4', label: 'שכונה' },
                      { value: '5', label: 'אוניבריסטה' },
                      { value: '6', label: 'מרכז רפואי' },
                      { value: '7', label: 'אוזר תעשיה' },
                      { value: '8', label: 'דוכן' }
                    ]}
                  />
                )}
                rules={{ required: true }}
              />

              <Controller
                name="branchName"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    appearance="plain"
                    {...field}
                    label="שם הסניף"
                    error={!!error}
                    helperText={error?.message}
                    multiline
                    type="text"
                  />
                )}
                rules={{ required: true }}
              />
              <Controller
                name="comment"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    {...field}
                    appearance="plain"
                    label="הערה"
                    error={!!error}
                    helperText={error?.message}
                    multiline
                    rows={2}
                  />
                )}
                rules={{ required: true }}
              />
            </Box>
          </FormProvider>
        </Dialog>
      );
    case PopUpTypes.REPORT_FORM:
      return (
        <Dialog open={true} onClose={handleClose} title="דווח על טעות במפה">
          {/* <ReportForm /> */}
        </Dialog>
      );
    case PopUpTypes.PRINT_MAP:
      return <MapPrint polygons={polygons} handleClose={handleClose} />;
    default:
      return <></>;
  }
};

export default EditPopUp;
